import { useIsSkiesTierColorEnabled } from '@/features/flags';
import { useTheme } from 'next-themes';
import { useEffect } from 'react';

const SKIES_CLASSES = ['skiesTier', 'skiesTierDark'] as const;

// next-themes `resolvedTheme` is the theme NAME (light | dark | newBrand |
// newBrandDark), so both dark names map to the dark override.
const DARK_RESOLVED_THEMES = new Set(['dark', 'newBrandDark']);

/**
 * Toggles the flag-gated sky-blue tier override (`release-heidi-2-skies-tier-color`).
 *
 * The `skiesTier` / `skiesTierDark` themes only redeclare the `tier-*` CSS
 * variables, so adding one as an ADDITIVE second class on `<html>`
 * (next-themes owns `.newBrand` / `.newBrandDark` and won't strip a class it
 * doesn't manage) recolors token-driven Pro/Practice/Together surfaces with no
 * call-site changes. The tier SVG glyphs read their `tier-*` token too, so they
 * flip with everything else.
 * Separate from `UserThemeManager` because that effect is ungated at boot, while
 * this one depends on the flag AND the resolved light/dark theme.
 *
 * Never pair these classes with a `data-theme` attribute — tw-colors treats it as
 * a variant boundary and would strip `newBrandDark:*` variants app-wide.
 */
export const TierColorThemeManager = () => {
  const isEnabled = useIsSkiesTierColorEnabled();
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove(...SKIES_CLASSES);

    if (!isEnabled) return;

    root.classList.add(
      DARK_RESOLVED_THEMES.has(resolvedTheme ?? '') ? 'skiesTierDark' : 'skiesTier'
    );
  }, [isEnabled, resolvedTheme]);

  return null;
};
